import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import i18n from 'simple-react-i18n'
import { Button, Card, CardContent, Grid, Typography } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'
import { round } from 'lodash'
import { CardTitle } from '../../../components/card/NewCard'
import { NewTable } from '../../../components/datatable/NewTable'
import NumberField from '../../../components/forms/NumberField'
import StationAction from '../../actions/StationAction'
import { STATION_TYPE_CONSTANT } from '../../constants/StationConstants'
import { MAX_WEIGHT, buildOptions, getAppliedWeights, parsePonderation } from './StationLinkOptionsUtils'

const getWeightedLinks = (stationType, associatedSites) => {
    if (stationType !== STATION_TYPE_CONSTANT.RESOURCE) {
        return []
    }
    const piezometers = associatedSites.filter(link => link.stationLinkedType === STATION_TYPE_CONSTANT.PIEZOMETRY)
    return piezometers.length ? piezometers : associatedSites.filter(link => link.stationLinkedType === STATION_TYPE_CONSTANT.HYDROMETRY)
}

const StationLinkWeightsPanel = ({ station }) => {
    const dispatch = useDispatch()

    const associatedSites = useSelector(store => store.StationReducer.associatedSites)

    const stationType = STATION_TYPE_CONSTANT[station?.typeName]
    const links = useMemo(() => getWeightedLinks(stationType, associatedSites), [stationType, associatedSites])
    const [weights, setWeights] = useState({})

    useEffect(() => {
        setWeights(links.reduce((acc, link) => ({ ...acc, [link.stationLinkedCode]: parsePonderation(link.options) }), {}))
    }, [links])

    const declaredTotal = links.reduce((acc, link) => acc + (weights[link.stationLinkedCode] ?? 0), 0)
    const appliedWeights = useMemo(() => getAppliedWeights(links.map(link => link.stationLinkedCode), weights), [links, weights])

    const rows = links.map(link => ({
        name: { value: link.stationLinkedName ? `[${link.stationLinkedCode}] ${link.stationLinkedName}` : link.stationLinkedCode },
        ponderation: {
            value: (
                <NumberField
                    value={weights[link.stationLinkedCode]}
                    onChange={v => setWeights(prev => ({ ...prev, [link.stationLinkedCode]: v }))}
                    floatValue
                    min={0}
                    max={MAX_WEIGHT}
                />
            ),
        },
        applied: { value: `${round(appliedWeights[link.stationLinkedCode] * 100, 1)} %`, align: 'center' },
    }))

    const headers = [
        { key: 'name', value: i18n.station, noSort: true },
        { key: 'ponderation', value: i18n.ponderation, noSort: true, width: 130 },
        { key: 'applied', value: i18n.appliedWeighting, noSort: true, width: 110 },
    ]

    const onSave = () => dispatch(StationAction.updateAssociatedSitesOptions(station.code, stationType, links.map(link => ({
        code: station.code,
        stationType,
        stationLinkedCode: link.stationLinkedCode,
        stationLinkedType: link.stationLinkedType,
        linkType: link.linkType,
        options: buildOptions(weights[link.stationLinkedCode]),
    }))))

    if (!links.length) {
        return null
    }

    return (
        <Card>
            <CardTitle title={i18n.ponderation} />
            <CardContent>
                <Grid container spacing={2} alignItems='center'>
                    <Grid size={12}>
                        <NewTable
                            rows={rows}
                            headers={headers}
                            rowsPerPageOptions={[{ value: 1000, label: '1000' }]}
                        />
                    </Grid>
                    <Grid size='grow'>
                        <Typography variant='body2' color={declaredTotal > MAX_WEIGHT ? 'error' : 'text.secondary'}>
                            {`${i18n.total} : ${round(declaredTotal * 100, 1)} %`}
                        </Typography>
                    </Grid>
                    <Grid size='auto'>
                        <Button variant='contained' onClick={onSave}>{i18n.register}</Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

StationLinkWeightsPanel.propTypes = {
    station: PropTypes.shape({
        code: PropTypes.string,
        typeName: PropTypes.string,
    }),
}

export default StationLinkWeightsPanel